/**
 * All media plans (or just mine): who, how much, when, status, client files.
 * Active plans by default (superseded versions hidden), newest first, 50 at a
 * time. Each row opens the plan from its advertiser name; the ⋯ menu holds
 * New version, Copy link and the Sheet.
 */

import React, { useMemo, useState } from 'react';
import { Button, EmptyState, RowActionsMenu, Spinner, tableStyles, type RowAction } from '../../ui';
import FilterChipGroup from '../FilterChipGroup';
import type { Notify } from './PlanBuilder';
import PlanStatusPill from './PlanStatusPill';
import { canMakeNewVersion } from './planStatus';
import type { MediaPlanDoc, MediaPlanStatus } from './types';
import { bannerStyle, fieldStyle, fmtBudget, fmtDate, mutedText } from './formStyles';

interface Props {
  plans: MediaPlanDoc[];
  loading: boolean;
  error: string | null;
  /** When the list was last fetched (ms), for "Updated 2:41 PM". */
  loadedAt: number | null;
  /** The fetch cap: a full page means older plans exist that aren't listed. */
  limit: number;
  mine: boolean;
  /** Who is looking, for "New version" (the newest version, by its creator or a manager). */
  currentUid: string | null;
  canManagePlans: boolean;
  onMineChange: (mine: boolean) => void;
  onOpen: (id: string) => void;
  onNew: () => void;
  onNewVersion: (plan: MediaPlanDoc) => void;
  onRefresh: () => void;
  notify: Notify;
}

type Scope = 'all' | 'mine';
type StatusFilter = 'active' | MediaPlanStatus | 'all';
type SortKey = 'created' | 'updated' | 'flight' | 'budget' | 'advertiser';

const SCOPE_OPTIONS: Array<{ value: Scope; label: string }> = [
  { value: 'all', label: 'All plans' },
  { value: 'mine', label: 'Mine' },
];
const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'shared', label: 'Shared' },
  { value: 'approved', label: 'Approved' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'all', label: 'All' },
];
const SORT_OPTIONS: Array<{ value: SortKey; label: string }> = [
  { value: 'created', label: 'Newest first' },
  { value: 'updated', label: 'Recently updated' },
  { value: 'flight', label: 'Flight start' },
  { value: 'budget', label: 'Largest budget' },
  { value: 'advertiser', label: 'Advertiser A–Z' },
];

export const PAGE_SIZE = 50;

export const planTiersLabel = (plan: MediaPlanDoc): string => plan.tiers.map((t) => fmtBudget(t.budgetUsd)).join(' / ');

const ownerName = (plan: MediaPlanDoc): string => plan.createdBy.name || plan.createdBy.email.split('@')[0];

const audienceCount = (plan: MediaPlanDoc): number => Math.max(1, plan.request.segments?.length ?? 0);

const maxBudget = (plan: MediaPlanDoc): number => Math.max(0, ...plan.tiers.map((t) => t.budgetUsd));

function compare(sort: SortKey): (a: MediaPlanDoc, b: MediaPlanDoc) => number {
  switch (sort) {
    case 'updated':
      return (a, b) => b.updatedAt.localeCompare(a.updatedAt);
    case 'flight':
      return (a, b) => a.request.flightStart.localeCompare(b.request.flightStart) || b.createdAt.localeCompare(a.createdAt);
    case 'budget':
      return (a, b) => maxBudget(b) - maxBudget(a) || b.createdAt.localeCompare(a.createdAt);
    case 'advertiser':
      return (a, b) => a.advertiser.name.localeCompare(b.advertiser.name) || b.version - a.version;
    default:
      return (a, b) => b.createdAt.localeCompare(a.createdAt);
  }
}

/** The plan's own link: this page with ?tab=media-plans&plan=<id>. */
export const planLink = (id: string): string => `${window.location.origin}${window.location.pathname}?tab=media-plans&plan=${encodeURIComponent(id)}`;

const PlanList: React.FC<Props> = ({ plans, loading, error, loadedAt, limit, mine, currentUid, canManagePlans, onMineChange, onOpen, onNew, onNewVersion, onRefresh, notify }) => {
  const [filter, setFilter] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [sort, setSort] = useState<SortKey>('created');
  const [shown, setShown] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return plans
      .filter((p) => (status === 'all' ? true : status === 'active' ? p.status !== 'superseded' : p.status === status))
      .filter((p) => {
        if (!q) return true;
        const haystack = [
          p.advertiser.name,
          p.request.objective,
          p.advertiser.categoryLabel,
          p.agencyLabel,
          p.createdBy.name,
          p.createdBy.email,
          ...(p.request.segments || []).map((s) => s.label),
        ];
        return haystack.some((v) => (v || '').toLowerCase().includes(q));
      })
      .sort(compare(sort));
  }, [plans, filter, status, sort]);

  const visible = filtered.slice(0, shown);
  const copyLink = (plan: MediaPlanDoc) => {
    if (!navigator.clipboard) {
      notify('error', 'Copy is not available in this browser');
      return;
    }
    navigator.clipboard.writeText(planLink(plan.id)).then(
      () => notify('success', 'Link copied', `${plan.advertiser.name} v${plan.version}`),
      () => notify('error', 'Could not copy the link'),
    );
  };

  const rowActions = (p: MediaPlanDoc): RowAction[] => {
    const sheet = [...p.exports].reverse().find((e) => e.kind === 'google_sheet');
    return [
      { label: 'Open', onSelect: () => onOpen(p.id) },
      ...(canMakeNewVersion(p, { uid: currentUid, canManage: canManagePlans }) ? [{ label: 'New version', onSelect: () => onNewVersion(p) }] : []),
      { label: 'Copy link', onSelect: () => copyLink(p) },
      ...(sheet ? [{ label: 'Open Google Sheet', onSelect: () => window.open(sheet.url, '_blank', 'noopener') }] : []),
    ];
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={styles.toolbar}>
        <div style={styles.toolbarGroup}>
          <FilterChipGroup<Scope> options={SCOPE_OPTIONS} value={mine ? 'mine' : 'all'} onChange={(v) => onMineChange(v === 'mine')} size="sm" tone="soft" />
          <span aria-hidden="true" style={styles.divider} />
          <FilterChipGroup<StatusFilter>
            options={STATUS_OPTIONS}
            value={status}
            onChange={(v) => {
              setStatus(v);
              setShown(PAGE_SIZE);
            }}
            size="sm"
            tone="soft"
          />
        </div>
        <div style={styles.toolbarGroup}>
          <input
            style={{ ...fieldStyle, width: 240 }}
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setShown(PAGE_SIZE);
            }}
            placeholder="Search advertiser, campaign, owner…"
            aria-label="Search media plans"
          />
          <select aria-label="Sort media plans" style={{ ...fieldStyle, width: 170, cursor: 'pointer' }} value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <Button size="sm" variant="ghost" onClick={onRefresh} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
          {loadedAt && <span style={mutedText}>Updated {new Date(loadedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>}
        </div>
      </div>

      {error && (
        <div role="alert" style={bannerStyle('danger')}>
          <span style={{ flex: 1 }}>Could not load media plans: {error}</span>
          <Button size="sm" variant="ghost" onClick={onRefresh}>
            Retry
          </Button>
        </div>
      )}

      {loading && plans.length === 0 ? (
        <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
          <Spinner label="Loading media plans…" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={plans.length === 0 ? (mine ? 'You haven’t built a media plan yet' : 'No media plans yet') : 'No plans match these filters'}
          subtitle={plans.length === 0 ? 'Pick an advertiser, set the budget, flight and audiences, and get a priced plan with rates from the advertiser’s history or the list.' : 'Try another status or search.'}
          cta={
            plans.length === 0 ? (
              <Button variant="primary" onClick={onNew}>
                + New media plan
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <div style={tableStyles.wrapScroll}>
            <table style={tableStyles.table}>
              <thead>
                <tr>
                  <th style={tableStyles.th}>Plan</th>
                  <th style={tableStyles.thNum}>Budget</th>
                  <th style={tableStyles.th}>Flight</th>
                  <th style={tableStyles.th}>Status</th>
                  <th style={tableStyles.th}>Client files</th>
                  <th style={tableStyles.th}>
                    <span style={visuallyHidden}>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((p) => {
                  const sheet = [...p.exports].reverse().find((e) => e.kind === 'google_sheet');
                  const pdfs = p.exports.filter((e) => e.kind === 'pdf').length;
                  const audiences = audienceCount(p);
                  const agency = p.agencyLabel;
                  return (
                    <tr key={p.id}>
                      <td style={tableStyles.td}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
                          <button type="button" style={styles.planButton} onClick={() => onOpen(p.id)}>
                            {p.advertiser.name}
                          </button>
                          <span style={mutedText}>v{p.version}</span>
                          {p.request.objective && <span style={{ fontSize: 13, color: 'var(--fg-2)' }}>· {p.request.objective}</span>}
                        </div>
                        <div style={mutedText}>
                          {[p.advertiser.categoryLabel, agency ? `via ${agency}` : null, `${audiences} audience${audiences === 1 ? '' : 's'}`, ownerName(p), fmtDate(p.createdAt)]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </td>
                      <td style={{ ...tableStyles.tdNum, fontWeight: 600 }}>{planTiersLabel(p)}</td>
                      <td style={tableStyles.td}>
                        {fmtDate(p.request.flightStart)} – {fmtDate(p.request.flightEnd)}
                      </td>
                      <td style={tableStyles.td}>
                        <PlanStatusPill status={p.status} />
                      </td>
                      <td style={tableStyles.td}>
                        {sheet ? (
                          <a href={sheet.url} target="_blank" rel="noopener noreferrer" style={styles.link}>
                            Sheet ↗
                          </a>
                        ) : (
                          <span style={mutedText}>No Sheet</span>
                        )}
                        {pdfs > 0 && <span style={{ ...mutedText, marginLeft: 8 }}>{pdfs} PDF{pdfs === 1 ? '' : 's'}</span>}
                      </td>
                      <td style={{ ...tableStyles.td, textAlign: 'right' }}>
                        <RowActionsMenu label={`${p.advertiser.name} v${p.version}`} actions={rowActions(p)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={styles.footer}>
            <span style={mutedText}>
              Showing {visible.length} of {filtered.length} plan{filtered.length === 1 ? '' : 's'}
            </span>
            {filtered.length > visible.length && (
              <Button size="sm" variant="subtle" onClick={() => setShown((n) => n + PAGE_SIZE)}>
                Show {Math.min(PAGE_SIZE, filtered.length - visible.length)} more
              </Button>
            )}
          </div>
        </>
      )}

      {plans.length >= limit && (
        <div role="status" style={bannerStyle('info')}>
          Only the {limit} most recent plans{mine ? ' you made' : ''} are listed. Search or filter to find one; older plans don’t appear here.
        </div>
      )}
    </div>
  );
};

const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const styles: Record<string, React.CSSProperties> = {
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  toolbarGroup: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  divider: { width: 1, height: 18, background: 'var(--border-1)' },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  planButton: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--fg-1)',
    textAlign: 'left',
  },
  link: { color: 'var(--fg-link)', fontWeight: 600, fontSize: 12.5, textDecoration: 'none' },
};

export default PlanList;
